// สแกนกระดาษคำตอบด้วยกล้องมือถือ — ส่องทีละแผ่น ได้คะแนนทันที
//
// อ่านทุกเฟรมใน Web Worker (omr/scanWorker.js) ด้วยตัวอ่านตัวเดียวกับหน้าสแกน PDF แต่หามุมแบบภาพกล้อง
// (readCameraFrame) ภาพไม่ถูกส่งขึ้นเซิร์ฟเวอร์ บันทึกเฉพาะคำตอบผ่าน endpoint เดิม (source = 'scan')
//
// กติกาการเก็บแผ่น
//   * ต้องอ่านได้ "นักเรียนคนเดิม คำตอบเดิม" สองเฟรมติดกัน — กันภาพเบลอตอนมือยังขยับ
//   * อ่านชัดทุกข้อ และยังไม่มีคำตอบเดิม (หรือเดิมตรงกัน) → บันทึกเองทันที เสียงดัง/สั่นให้รู้
//   * มีข้อไม่แน่ใจ หรือมีคำตอบเดิมที่ต่างกัน → หยุดภาพให้ครูดูก่อน ไม่บันทึกเอง
//   * แผ่นเดิมยังอยู่หน้ากล้องหลังบันทึก → ไม่ทำซ้ำ จนกว่าจะเห็นแผ่นอื่นหรือไม่เห็นกระดาษ
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Form, ListGroup } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { API, readerText as toText, savedText, fmt, isObjective, labelSet, roomLabel, showError, matchSpare } from './examUtils'
import { thumbnail } from './omr/loadPages'
import SheetOverlay, { UncertainList } from './SheetOverlay'

const FRAME_MS = 350        // เว้นระหว่างเฟรม — ตัวอ่านบนมือถือใช้ราว 200-400 มส. ถี่กว่านี้ก็แค่รอคิว
const MAX_SIDE = 1600       // ย่อเฟรมก่อนอ่าน — ละเอียดกว่านี้ไม่ช่วยอ่านช่อง 4.8 มม. แต่ช้าลงมาก
const STABLE_FRAMES = 2


const tone = (ok) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    const ctx = new Ctx()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.frequency.value = ok ? 1046 : 330
    gain.gain.value = 0.15
    osc.connect(gain).connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3))
    osc.onended = () => ctx.close()
  } catch { /* ไม่มีเสียงก็ไม่เป็นไร */ }
  try { navigator.vibrate?.(ok ? 60 : [80, 60, 80]) } catch { /* ไม่รองรับ */ }
}

export default function ExamCamera() {
  const { examId } = useParams()
  const [, setSkin] = useState('')
  const [data, setData] = useState(null)
  const [running, setRunning] = useState(false)
  const [camError, setCamError] = useState(null)
  const [status, setStatus] = useState({ tone: 'secondary', text: 'กดเปิดกล้อง แล้วส่องกระดาษคำตอบให้เห็นสี่เหลี่ยมดำครบ 4 มุม' })
  const [quad, setQuad] = useState(null)            // มุมที่เห็นในเฟรมล่าสุด (พิกัดเฟรม) ไว้วาดกรอบ
  const [frameSize, setFrameSize] = useState(null)
  const [review, setReview] = useState(null)        // แผ่นที่หยุดรอครูดู
  const [history, setHistory] = useState([])
  const [torch, setTorch] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const workerRef = useRef(null)
  const canvasRef = useRef(null)
  const busyRef = useRef(false)
  const timerRef = useRef(null)
  const stableRef = useRef({ key: null, count: 0 })
  const doneRef = useRef(null)                      // study_id ที่เพิ่งจัดการไป — ไม่ทำซ้ำขณะแผ่นเดิมยังอยู่หน้ากล้อง
  const pausedRef = useRef(false)
  const wakeRef = useRef(null)
  const dataRef = useRef(null)

  const load = useCallback(() => axiosInstance.get(`${API}/exams/${examId}/results`)
    .then((res) => setData(res.data))
    .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ')), [examId])
  useEffect(() => { load() }, [load])
  useEffect(() => { dataRef.current = data }, [data])

  const objective = useMemo(() => (data ? data.questions.filter(isObjective) : []), [data])
  const objectiveRef = useRef(objective)
  useEffect(() => { objectiveRef.current = objective }, [objective])

  // ---- บันทึก ----

  const saveSheet = useCallback(async (sheet, text) => {
    const d = dataRef.current
    const student = d.students.find((s) => s.study_id === sheet.studyId)
    const chars = [...text]
    const obj = objectiveRef.current
    const answers = Object.fromEntries(obj.map((q, i) => [q.eq_no, chars[i] ?? '']))
    const res = await axiosInstance.put(`${API}/exams/${examId}/rooms/${student.teach_id}/responses`, {
      source: 'scan',
      responses: [{ study_id: sheet.studyId, answers }],
    })
    const score = res.data.scores?.[sheet.studyId]
    // โหลดคำตอบที่บันทึกแล้วจาก server แทนการประกอบเองในหน้านี้ — สแกนซ้ำจะได้เทียบกับของจริง
    await load()
    setHistory((h) => [{ at: new Date(), student, score, total: res.data.total_points, edited: text !== sheet.text }, ...h].slice(0, 200))
    return { student, score, total: res.data.total_points }
  }, [examId, load])

  // ---- ผลของแต่ละเฟรม ----

  const onResult = useCallback(async (res, canvas) => {
    const d = dataRef.current
    if (!d || pausedRef.current) return
    setQuad(res.markers || null)

    if (!res.found) {
      stableRef.current = { key: null, count: 0 }
      doneRef.current = null
      setStatus({ tone: 'secondary', text: 'หากระดาษ… ส่องให้เห็นสี่เหลี่ยมดำครบ 4 มุม' })
      return
    }
    if (res.wrongExam) {
      setStatus({ tone: 'danger', text: 'เป็นกระดาษของข้อสอบชุดอื่น' })
      return
    }
    if (!res.ok) {
      setStatus({ tone: 'warning', text: res.error })
      return
    }
    // กระดาษสำรอง: หานักเรียนจากเลขประจำตัวที่ฝน — ไม่ตรงคนเดียวพอดีให้ครูไปใช้หน้าสแกน PDF เลือกเอง
    let student
    if (res.spare) {
      const match = matchSpare(res, d.students)
      if (!match.student) {
        setStatus({ tone: 'warning', text: match.problem })
        return
      }
      student = match.student
    } else {
      student = d.students.find((s) => s.study_id === res.studyId)
    }
    if (!student) {
      setStatus({ tone: 'danger', text: 'นักเรียนคนนี้ไม่ได้อยู่ในห้องที่ใช้ข้อสอบนี้' })
      return
    }
    const studyId = student.study_id
    if (doneRef.current === studyId) return   // แผ่นเดิมที่เพิ่งจัดการไป — รอเปลี่ยนแผ่น

    const text = toText(res.answers, objectiveRef.current, labelSet(d.exam))
    const key = `${studyId}|${text}`
    stableRef.current = stableRef.current.key === key
      ? { key, count: stableRef.current.count + 1 }
      : { key, count: 1 }
    if (stableRef.current.count < STABLE_FRAMES) {
      setStatus({ tone: 'info', text: `พบ ${student.st_name} — ถือนิ่งๆ` })
      return
    }
    stableRef.current = { key: null, count: 0 }

    const before = student.answers ? savedText(student.answers, objectiveRef.current, labelSet(d.exam)) : null
    const diff = before === null ? 0 : [...text].filter((ch, i) => ch !== before[i]).length
    const sheet = {
      studyId, text, answers: res.answers, uncertain: res.uncertain,
      rows: res.rows, markers: res.markers, student, diff, hadAnswers: before !== null,
      spare: !!res.spare, idRead: res.idRead || null, idRows: res.idRows || [],
    }

    if (before !== null && diff === 0 && !res.uncertain.length) {
      doneRef.current = studyId
      setStatus({ tone: 'success', text: `${student.st_name} — บันทึกไว้แล้ว ตรงกัน${student.score ? ` (${fmt(student.score.total)} คะแนน)` : ''}` })
      return
    }
    // กระดาษสำรองหยุดให้ครูดูทุกแผ่น — เลขที่ฝนไม่มี CRC ฝนผิดหลักเดียวตรงกับเพื่อนได้
    if (res.spare || res.uncertain.length || (before !== null && diff > 0)) {
      // หยุดภาพให้ครูดู — ภาพย่อจากเฟรมนี้ (พิกัดช่องฝนเป็นพิกัดเฟรม ใช้ scale ของภาพย่อวาดทับได้ตรง)
      pausedRef.current = true
      doneRef.current = studyId
      tone(false)
      setReview({ ...sheet, thumb: thumbnail(canvas), edited: text })
      setStatus({
        tone: 'warning',
        text: res.spare
          ? `กระดาษสำรอง — ฝนเลข ${res.idRead} ตรงกับ ${student.st_name} ตรวจชื่อที่เขียนก่อนบันทึก`
          : `${student.st_name} — ต้องตรวจก่อนบันทึก`,
      })
      return
    }
    doneRef.current = studyId
    try {
      const out = await saveSheet(sheet, text)
      tone(true)
      setStatus({
        tone: 'success',
        text: `✓ ${student.st_name} ${roomLabel(student)} — ${out.score ? `${fmt(out.score.total)}/${fmt(out.total)}` : 'บันทึกแล้ว'}${out.score && !out.score.complete ? ' (อัตนัยยังไม่ตรวจ)' : ''}`,
      })
    } catch (err) {
      doneRef.current = null
      tone(false)
      setStatus({ tone: 'danger', text: `บันทึกไม่สำเร็จ: ${err?.response?.data?.message || err.message}` })
    }
  }, [saveSheet])

  // ---- วนอ่านเฟรม ----

  const tick = useCallback(() => {
    timerRef.current = setTimeout(tick, FRAME_MS)
    const video = videoRef.current
    const d = dataRef.current
    if (busyRef.current || pausedRef.current || !video || !d || video.readyState < 2 || document.hidden) return
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!vw || !vh) return
    const scale = Math.min(1, MAX_SIDE / Math.max(vw, vh))
    const w = Math.round(vw * scale)
    const h = Math.round(vh * scale)
    const canvas = canvasRef.current || (canvasRef.current = document.createElement('canvas'))
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    ctx.drawImage(video, 0, 0, w, h)
    const rgba = ctx.getImageData(0, 0, w, h).data.buffer
    busyRef.current = true
    setFrameSize({ w, h })
    const id = Date.now()
    const worker = workerRef.current
    const handle = (e) => {
      if (e.data.id !== id) return
      worker.removeEventListener('message', handle)
      busyRef.current = false
      onResult(e.data.res, canvas)
    }
    worker.addEventListener('message', handle)
    worker.postMessage({ id, width: w, height: h, rgba, exam: d.exam, questions: d.questions }, [rgba])
  }, [onResult])

  const stop = useCallback(() => {
    clearTimeout(timerRef.current)
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    workerRef.current?.terminate()
    workerRef.current = null
    busyRef.current = false
    wakeRef.current?.release?.().catch(() => {})
    wakeRef.current = null
    setRunning(false)
    setQuad(null)
    setTorch(false)
  }, [])

  const start = async () => {
    setCamError(null)
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setCamError('เบราว์เซอร์นี้เปิดกล้องไม่ได้ — ต้องเปิดผ่าน https (หรือ localhost) และใช้ Chrome/Safari รุ่นใหม่')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      })
      streamRef.current = stream
      const video = videoRef.current
      video.srcObject = stream
      await video.play()
      const track = stream.getVideoTracks()[0]
      const caps = track.getCapabilities?.() || {}
      setTorchSupported(!!caps.torch)
      if (caps.focusMode?.includes('continuous')) {
        track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {})
      }
      workerRef.current = new Worker(new URL('./omr/scanWorker.js', import.meta.url))
      try { wakeRef.current = await navigator.wakeLock?.request('screen') } catch { /* จอดับเองได้ ไม่เป็นไร */ }
      pausedRef.current = false
      setRunning(true)
      tick()
    } catch (err) {
      setCamError(err.name === 'NotAllowedError'
        ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — เปิดสิทธิ์กล้องให้เว็บนี้ในการตั้งค่าเบราว์เซอร์'
        : `เปิดกล้องไม่ได้: ${err.message}`)
      stop()
    }
  }

  useEffect(() => stop, [stop])

  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0]
    if (!track) return
    try {
      await track.applyConstraints({ advanced: [{ torch: !torch }] })
      setTorch(!torch)
    } catch { setTorchSupported(false) }
  }

  const closeReview = (saved) => {
    setReview(null)
    pausedRef.current = false
    if (!saved) doneRef.current = review?.studyId ?? null
  }

  const confirmReview = async () => {
    try {
      const out = await saveSheet(review, review.edited)
      tone(true)
      setStatus({ tone: 'success', text: `✓ ${review.student.st_name} — ${out.score ? `${fmt(out.score.total)}/${fmt(out.total)}` : 'บันทึกแล้ว'}` })
      closeReview(true)
    } catch (err) {
      showError(err, 'บันทึกไม่สำเร็จ')
    }
  }

  if (!data) return (<><Header onSkin={setSkin} /><div className="main main-app p-2 p-lg-4" /></>)

  const answeredCount = data.students.filter((s) => s.answers).length
  const activeCount = data.students.filter((s) => String(s.st_status) !== '0' && s.transfer_room_id == null).length

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-start gap-2 mb-2">
          <div className="me-auto">
            <Link to={`/teaching/exams/${examId}`} className="fs-sm"><i className="ri-arrow-left-line"></i> {data.exam.exam_name}</Link>
            <h4 className="main-title mb-0">สแกนด้วยกล้อง</h4>
            <div className="fs-sm text-secondary">มีคำตอบแล้ว {answeredCount}/{activeCount} คน · สแกนรอบนี้ {history.length} แผ่น</div>
          </div>
          {running && torchSupported && (
            <Button size="sm" variant={torch ? 'warning' : 'outline-secondary'} onClick={toggleTorch}>
              <i className="ri-flashlight-line"></i>
            </Button>
          )}
          {running
            ? <Button size="sm" variant="outline-danger" onClick={stop}><i className="ri-stop-line"></i> ปิดกล้อง</Button>
            : <Button size="sm" onClick={start}><i className="ri-camera-line"></i> เปิดกล้อง</Button>}
        </div>

        {camError && <Alert variant="danger" className="fs-sm">{camError}</Alert>}

        <div className="position-relative bg-dark rounded overflow-hidden mx-auto mb-2" style={{ maxWidth: 520, display: running && !review ? 'block' : 'none' }}>
          <video ref={videoRef} playsInline muted className="w-100 d-block" />
          {quad && frameSize && (
            <svg className="position-absolute top-0 start-0 w-100 h-100" viewBox={`0 0 ${frameSize.w} ${frameSize.h}`} preserveAspectRatio="none">
              <polygon points={quad.map((m) => `${m.x},${m.y}`).join(' ')} fill="none"
                stroke={status.tone === 'success' ? '#20c997' : status.tone === 'danger' ? '#dc3545' : '#ffc107'} strokeWidth={Math.max(3, frameSize.w / 200)} />
            </svg>
          )}
        </div>

        <Alert variant={status.tone === 'secondary' ? 'light' : status.tone} className="py-2 mb-3 fw-semibold text-center mx-auto" style={{ maxWidth: 520 }}>
          {status.text}
        </Alert>

        {review && (
          <Card className="card-one mb-3 mx-auto" style={{ maxWidth: 720 }}>
            <Card.Header>
              <div className="fw-semibold">{review.student.st_name} · {roomLabel(review.student)} เลขที่ {review.student.seat}</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {review.spare && <Badge bg="primary">กระดาษสำรอง · ฝนเลข {review.idRead} — เทียบกับชื่อที่เขียนบนแผ่น</Badge>}
                {review.uncertain.length > 0 && <Badge bg="warning" text="dark">ไม่แน่ใจ {review.uncertain.length} ข้อ: {review.uncertain.join(', ')}</Badge>}
                {review.hadAnswers && review.diff > 0 && <Badge bg="danger">มีคำตอบเดิมอยู่แล้ว ต่าง {review.diff} ข้อ — จะถูกแทนที่</Badge>}
              </div>
            </Card.Header>
            <Card.Body>
              <SheetOverlay thumb={review.thumb} markers={review.markers} rows={review.rows} text={review.edited}
                objective={objective} uncertain={review.uncertain} idRows={review.idRows} labels={labelSet(data?.exam)} />
              <UncertainList uncertain={review.uncertain} rows={review.rows} labels={labelSet(data?.exam)} />
              <Form.Label className="fs-sm mt-2">คำตอบ (แก้ได้)</Form.Label>
              <Form.Control size="sm" className="font-monospace" style={{ letterSpacing: 2 }} value={review.edited}
                maxLength={objective.length} onChange={(e) => setReview({ ...review, edited: e.target.value })} />
            </Card.Body>
            <Card.Footer className="d-flex gap-2">
              <Button variant="light" className="flex-fill" onClick={() => closeReview(false)}>ข้าม</Button>
              <Button className="flex-fill" onClick={confirmReview}><i className="ri-check-line"></i> บันทึก</Button>
            </Card.Footer>
          </Card>
        )}

        {history.length > 0 && (
          <Card className="card-one mx-auto" style={{ maxWidth: 720 }}>
            <Card.Header className="fw-semibold">สแกนรอบนี้</Card.Header>
            <ListGroup variant="flush">
              {history.map((h, i) => (
                <ListGroup.Item key={i} className="d-flex align-items-center gap-2">
                  <div className="me-auto">
                    <div>{h.student.st_name}</div>
                    <div className="fs-xs text-secondary">
                      {roomLabel(h.student)} เลขที่ {h.student.seat} · {h.at.toTimeString().slice(0, 5)}{h.edited ? ' · แก้คำตอบเอง' : ''}
                    </div>
                  </div>
                  <b>{h.score ? `${fmt(h.score.total)}/${fmt(h.total)}` : '-'}</b>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        )}

        {!running && (
          <div className="fs-xs text-secondary text-center mt-3 mx-auto" style={{ maxWidth: 520 }}>
            วางกระดาษบนพื้นเรียบสีเข้ม แสงสว่างสม่ำเสมอ ถือมือถือให้เห็นกระดาษทั้งแผ่น ·
            อ่านในเครื่องนี้ ไม่ได้ส่งภาพขึ้นเซิร์ฟเวอร์ · เปลี่ยนแผ่นได้เลยหลังได้ยินเสียง
          </div>
        )}
        <Footer />
      </div>
    </>
  )
}
